import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useForm, Controller } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import Typography from "../../../Utility/Typography";
import Label from "../../../Utility/Label/Label";
import Button from "../../../Utility/Button/Button";
import FormSubmitButton from "../../../Utility/Button/FormSubmitButton";
import Input from "../../../Utility/Input/Input";
import Checkbox from "../../../Utility/Checkbox/Checkbox";
import CommonModal from "../../../Utility/CommonModel/CommonModal";
import ErrorMsg from "../../../Utility/ErrorMsg/ErrorMsg";
import {
  createSaasMasterModule,
  updateSaasMasterModule,
} from "../../../Redux/Thunks/saasMasterModulesThunk";
import {
  clearSaasMasterModuleMutationFeedback,
  selectSaasMasterModulesSaveError,
  selectSaasMasterModulesSaving,
} from "../../../Redux/Slices/saasMasterModulesSlice";
import { getApiErrorMessage } from "../../../utils/apiErrorMessage";
import { showSuccess } from "../../../utils/toast";
import { SaasModuleMasterSchema } from "../../../FormsValidations/Validations";

const INPUT_FIELD_CLASS =
  "w-full text-sm border border-border-primary rounded-sm px-3 py-1.5 text-text-primary bg-surface-primary";

/**
 * Add / edit master SaaS module (POST or PUT).
 */
export default function SaasModuleMasterModal({
  isOpen,
  onClose,
  onSaved,
  /** Full row from GET list when editing; null for create. */
  existingModule = null,
}) {
  const dispatch = useDispatch();
  const saving = useSelector(selectSaasMasterModulesSaving);
  const saveErrorFromStore = useSelector(selectSaasMasterModulesSaveError);
  const [submitError, setSubmitError] = useState("");

  const {
    register,
    handleSubmit,
    control,
    reset,
    formState: { errors },
  } = useForm({
    resolver: yupResolver(SaasModuleMasterSchema),
    defaultValues: {
      name: "",
      isActive: true,
    },
  });

  const isEdit = Boolean(existingModule?.id);

  useEffect(() => {
    if (!isOpen) return;
    dispatch(clearSaasMasterModuleMutationFeedback());
    setSubmitError("");
    if (existingModule?.id) {
      const v = existingModule.isActive;
      reset({
        name: String(existingModule.name ?? ""),
        isActive: v === true || v === 1 || v === "1",
      });
    } else {
      reset({ name: "", isActive: true });
    }
  }, [isOpen, existingModule, dispatch, reset]);

  const resolvedSortOrder = () => {
    if (isEdit) {
      const n = Number(existingModule?.sortOrder);
      return Number.isFinite(n) ? n : 0;
    }
    return 0;
  };

  const handleClose = () => {
    if (!saving) onClose();
  };

  const onSubmit = async (data) => {
    if (saving) return;
    setSubmitError("");
    const trimmed = String(data.name ?? "").trim();
    const sortOrder = resolvedSortOrder();

    try {
      if (isEdit) {
        await dispatch(
          updateSaasMasterModule({
            id: existingModule.id,
            name: trimmed,
            sortOrder,
            isActive: Boolean(data.isActive),
          })
        ).unwrap();
      } else {
        await dispatch(
          createSaasMasterModule({
            name: trimmed,
            sortOrder,
            isActive: Boolean(data.isActive),
          })
        ).unwrap();
      }
      showSuccess(isEdit ? "SaaS module updated." : "SaaS module created.");
      onSaved?.();
      onClose();
    } catch (error) {
      setSubmitError(
        getApiErrorMessage(error, isEdit ? "Failed to update module." : "Failed to create module.")
      );
    }
  };

  const apiError = submitError || saveErrorFromStore;

  return (
    <CommonModal
      isOpen={isOpen}
      onClose={handleClose}
      title={isEdit ? "Update SaaS module" : "Add SaaS module"}
      width="max-w-md"
      loading={saving}
    >
      <form onSubmit={handleSubmit(onSubmit)} className="space-y-4" noValidate>
        {apiError ? (
          <div className="rounded-md border border-red-200 bg-red-50 px-3 py-2">
            <Typography className="text-sm text-red-700">{apiError}</Typography>
          </div>
        ) : null}

        <div>
          <Label htmlFor="saas-module-name" required>
            Name
          </Label>
          <Input
            id="saas-module-name"
            type="text"
            placeholder="e.g. Billing"
            disabled={saving}
            noWrapper
            label=""
            {...register("name")}
            className={`${INPUT_FIELD_CLASS} ${errors.name ? "border-badge-danger" : ""}`}
            error={errors.name?.message}
          />
          <ErrorMsg message={errors.name?.message} className="mt-1" />
        </div>

        <Controller
          name="isActive"
          control={control}
          render={({ field }) => (
            <Checkbox
              name="isActive"
              label="Active"
              checked={field.value}
              onChange={(e) => field.onChange(e.target.checked)}
              disabled={saving}
            />
          )}
        />

        <div className="mt-6 flex justify-end gap-2 border-t border-border-primary pt-4">
          <Button
            variant=""
            className="btn-reset"
            type="button"
            onClick={handleClose}
            disabled={saving}
          >
            Cancel
          </Button>
          <FormSubmitButton
            type="submit"
            variant="primary"
            idleIconClass=""
            disabled={saving}
            loading={saving}
            label={isEdit ? "Update" : "Create"}
          />
        </div>
      </form>
    </CommonModal>
  );
}
